(() => {
  // Safe when the section asset is emitted more than once on the same page.
  if (customElements.get('ks-instagram-gallery')) return;
class KsInstagramGallery extends HTMLElement {
  constructor() {
    super();
    this.swiperInstance = null;
    this.intersectionObserver = null;
    this.activityObserver = null;
    this.hasStarted = false;
    this.mountId = 0;
    this.abortController = null;
    this.swiperJsPath = this.dataset.vendorJsFile;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  connectedCallback() {
    if (this.hasStarted) return;
    this.mountId += 1;
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.swiperJsPath = this.dataset.vendorJsFile;
    this.root = this.closest('.ks-instagram-gallery');
    this.setupEntryReveal();

    if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      this.start();
      return;
    }

    this.intersectionObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = null;
      this.start();
    }, { rootMargin: '420px 0px' });

    this.intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.mountId += 1;
    this.abortController?.abort();
    this.abortController = null;
    this.hasStarted = false;
    this.intersectionObserver?.disconnect();
    this.activityObserver?.disconnect();
    this.entryObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.intersectionObserver = null;
    this.activityObserver = null;
    this.entryObserver = null;
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  setupEntryReveal() {
    if (!this.root?.classList.contains('ksig-motion-armed')) return;
    const mountId = this.mountId;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (!this.root?.isConnected || mountId !== this.mountId) return;
      if (!('IntersectionObserver' in window) || window.Shopify?.designMode) {
        this.root.classList.add('ksig-in-view');
        return;
      }
      this.entryObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        this.root.classList.toggle('ksig-in-view', entry.isIntersecting);
      }, { threshold: 0.1, rootMargin: '0px 0px -5% 0px' });
      this.entryObserver.observe(this.root);
    }));
  }

  async start() {
    if (this.hasStarted) return;
    this.hasStarted = true;
    const mountId = this.mountId;
    try {
      await this.ensureSwiper();
      if (!this.isConnected || this.swiperInstance || mountId !== this.mountId) return;
      this.init();
      this.initActivityObserver();
      this.setupThemeEditor();
    } catch (error) {
      if (mountId !== this.mountId) return;
      this.hasStarted = false;
      console.error('Instagram Gallery Error:', error);
    }
  }

  ensureSwiper() {
    if (window.Swiper) return Promise.resolve();
    if (window.__bnSwiperPromise) return window.__bnSwiperPromise;
    if (!this.swiperJsPath) return Promise.reject(new Error('Swiper asset URL is missing.'));

    window.__bnSwiperPromise = this.loadScript(this.swiperJsPath)
      .catch((error) => { window.__bnSwiperPromise = null; throw error; });
    return window.__bnSwiperPromise;
  }

  loadScript(src, id = 'bn-swiper-vendor') {
    return new Promise((resolve, reject) => {
      if (!src) { reject(new Error('Swiper asset URL is missing.')); return; }
      if (window.Swiper) { resolve(); return; }
      const existing = document.getElementById(id) || document.getElementById('swiper-js');
      const script = existing || document.createElement('script');
      let timeout;
      const cleanup = () => {
        clearTimeout(timeout);
        script.removeEventListener('load', loaded);
        script.removeEventListener('error', failed);
      };
      const failed = () => {
        cleanup();
        // A failed script cannot fire a future load event. Remove it so retry can work.
        script.remove();
        reject(new Error('Swiper asset could not be loaded.'));
      };
      const loaded = () => {
        if (!window.Swiper) { failed(); return; }
        cleanup();
        resolve();
      };
      script.addEventListener('load', loaded, { once: true });
      script.addEventListener('error', failed, { once: true });
      timeout = setTimeout(failed, 20000);
      if (!existing) {
        script.src = src;
        script.id = id;
        script.async = true;
        document.head.appendChild(script);
      }
    });
  }

  getAutoplayDelayMs() {
    const seconds = Number.parseFloat(this.dataset.sliderAutoplay || '0');
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
  }
  getTransitionSpeedMs() {
    const speed = Number.parseInt(this.dataset.sliderSpeed || '800', 10);
    return Number.isFinite(speed) && speed >= 0 ? speed : 800;
  }
  getTransitionEasing() {
    const presets = {
      smooth: 'cubic-bezier(0.22, 1, 0.36, 1)',
      balanced: 'cubic-bezier(0.4, 0, 0.2, 1)',
      cinematic: 'cubic-bezier(0.16, 1, 0.3, 1)',
      linear: 'linear',
    };
    return presets[this.dataset.sliderEasing] || presets.smooth;
  }
  getSpacing(mobile = true) {
    if (this.dataset.sliderSpacing === 'false') return 0;
    return Number(mobile ? this.dataset.swiperSpacingMobile : this.dataset.swiperSpacingDesktop) || (mobile ? 10 : 20);
  }

  init() {
    if (typeof window.Swiper === 'undefined' || this.swiperInstance) return;

    const autoplayDelay = this.getAutoplayDelayMs();
    const transitionSpeed = this.getTransitionSpeedMs();
    const transitionEasing = this.getTransitionEasing();
    const pauseOnMouseEnter = this.dataset.pauseOnHover !== 'false';
    const slidesMobile = Math.max(1, Number.parseFloat(this.dataset.slidesMobile || '1.25'));
    const slidesTablet = Math.max(1, Number.parseInt(this.dataset.slidesTablet || '2', 10));
    const slidesDesktop = Math.max(2, Number.parseInt(this.dataset.slidesDesktop || '4', 10));

    this.style.setProperty('--swiper-wrapper-transition-timing-function', transitionEasing);
    this.style.setProperty('--ksig-transition-easing', transitionEasing);

    const autoplayOptions = autoplayDelay > 0 ? {
      delay: autoplayDelay,
      pauseOnMouseEnter,
      disableOnInteraction: false,
      waitForTransition: true,
    } : false;

    const prevEl = this.querySelector('.ksig-pager-prev');
    const nextEl = this.querySelector('.ksig-pager-next');

    this.swiperInstance = new window.Swiper(this, {
      speed: transitionSpeed,
      centeredSlides: true,
      loop: true,
      autoplay: autoplayOptions,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      navigation: prevEl && nextEl ? { prevEl, nextEl } : undefined,
      mousewheel: { enabled: true, forceToAxis: true },
      keyboard: { enabled: true, onlyInViewport: true },
      a11y: {
        enabled: true,
        prevSlideMessage: 'Publicación anterior',
        nextSlideMessage: 'Publicación siguiente',
        firstSlideMessage: 'Primera publicación',
        lastSlideMessage: 'Última publicación',
      },
      grabCursor: true,
      breakpoints: {
        0: { slidesPerView: slidesMobile, spaceBetween: this.getSpacing(true) },
        750: { slidesPerView: slidesTablet, spaceBetween: this.getSpacing(true) },
        990: { slidesPerView: slidesDesktop, spaceBetween: this.getSpacing(false) },
      },
      on: {
        init: () => { this.classList.add('is-ready'); this.updateStackClasses(); this.updatePager(); },
        imagesReady: () => { this.classList.add('is-ready'); this.updateStackClasses(); },
        slideChange: () => { this.updateStackClasses(); this.updatePager(); },
        realIndexChange: () => { this.updateStackClasses(); this.updatePager(); },
        resize: () => this.updateStackClasses(),
      },
    });
  }

  updateStackClasses() {
    if (!this.swiperInstance) return;
    const slides = Array.from(this.swiperInstance.slides || []);
    const count = slides.length;
    if (!count) return;

    slides.forEach((slide) => {
      slide.classList.remove('ksig-stack-prev-1', 'ksig-stack-next-1', 'ksig-stack-prev-2', 'ksig-stack-next-2');
    });

    const active = Math.max(0, Number(this.swiperInstance.activeIndex) || 0);
    const at = (offset) => slides[(active + offset + count) % count];
    at(-1)?.classList.add('ksig-stack-prev-1');
    at(1)?.classList.add('ksig-stack-next-1');
    if (count >= 5) {
      at(-2)?.classList.add('ksig-stack-prev-2');
      at(2)?.classList.add('ksig-stack-next-2');
    }
  }

  updatePager() {
    const current = this.querySelector('[data-ksig-current]');
    const total = this.querySelector('[data-ksig-total]');
    const progress = this.querySelector('[data-ksig-progress]');
    const count = Math.max(1, Number.parseInt(this.dataset.total || '0', 10) || this.querySelectorAll('.swiper-wrapper > .swiper-slide:not(.swiper-slide-duplicate)').length || this.querySelectorAll('.swiper-wrapper > .swiper-slide').length);
    const index = (this.swiperInstance?.realIndex || 0) + 1;
    if (current) current.textContent = String(index).padStart(2, '0');
    if (total) total.textContent = String(count).padStart(2, '0');
    if (progress) progress.style.width = `${Math.min(100, (index / Math.max(1, count)) * 100)}%`;
  }

  initActivityObserver() {
    if (!this.swiperInstance || window.Shopify?.designMode || !('IntersectionObserver' in window)) return;
    this.activityObserver = new IntersectionObserver((entries) => {
      const isVisible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0.05);
      this.setAutoplayActive(isVisible && !document.hidden);
    }, { threshold: [0, 0.05, 0.25] });
    this.activityObserver.observe(this);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.handleVisibilityChange();
  }

  setAutoplayActive(active) {
    if (!this.swiperInstance?.autoplay || this.getAutoplayDelayMs() === 0) return;
    if (active) this.swiperInstance.autoplay.start();
    else this.swiperInstance.autoplay.stop();
  }
  handleVisibilityChange() {
    const rect = this.getBoundingClientRect();
    this.setAutoplayActive(!document.hidden && rect.bottom > 0 && rect.top < window.innerHeight);
  }

  setupThemeEditor() {
    const signal = this.abortController.signal;
    this.addEventListener('shopify:block:select', (event) => {
      const slide = event.target?.closest?.('.swiper-slide');
      if (!slide || !this.swiperInstance) return;
      const slides = Array.from(this.querySelectorAll('.swiper-wrapper > .swiper-slide:not(.swiper-slide-duplicate)'));
      const id = slide.dataset.slideId;
      const index = Math.max(0, slides.findIndex((item) => item.dataset.slideId === id));
      this.swiperInstance.slideToLoop(index, this.reducedMotion.matches ? 420 : this.getTransitionSpeedMs());
    }, { signal });

    this.root?.addEventListener('shopify:section:select', () => {
      if (!this.root?.classList.contains('ksig-motion-armed')) return;
      this.root.classList.remove('ksig-in-view');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!signal.aborted && this.root?.isConnected) this.root.classList.add('ksig-in-view');
      }));
    }, { signal });
  }
}

if (!customElements.get('ks-instagram-gallery')) customElements.define('ks-instagram-gallery', KsInstagramGallery);

})();
